/**
 * グループ設定
 * 各グループで使用カードが異なり、交流・パートナーは同グループ内のみ、
 * 対戦は別グループとのみ行う
 */

import type { CardPoolConfig } from '../utils/CardFilter';

/**
 * グループID型
 */
export type GroupId = string;

/**
 * 戦闘設定（非対称進化モード用）
 */
export interface BattleConfig {
  /** グループ内戦闘を行うか */
  internalBattle: boolean;
  /** グループ外戦闘を行うか */
  externalBattle: boolean;
  /** グループ内勝敗を淘汰判定に使うか */
  recordInternal: boolean;
  /** グループ外勝敗を淘汰判定に使うか */
  recordExternal: boolean;
}

/**
 * リスポーン時のデッキ継承設定
 */
export interface RespawnConfig {
  /** 死亡NPC本人の上位カード継承率 (0.0-1.0) */
  selfCardRatio: number;
  /** グループ内上位NPCからの継承率 (0.0-1.0) */
  groupCardRatio: number;
  /** カードプールからのランダム率 (0.0-1.0) */
  randomCardRatio: number;
}

/**
 * 人口制御モード
 */
export type PopulationMode = 'fixed' | 'natural';

/**
 * グループ設定
 */
export interface GroupConfig {
  id: GroupId;              // 'A', 'B', 'C' など
  name: string;             // '炎水グループ', '光闘グループ' など
  cardPoolConfig: CardPoolConfig;  // このグループのカードプール設定
  initialNpcCount: number;  // 初期NPC数
  color: string;            // 表示色（UI用）

  // === 非対称進化モード用（オプション） ===
  /** 戦闘設定 */
  battleConfig?: BattleConfig;
  /** 人口制御モード */
  populationMode?: PopulationMode;
  /** 固定人口数（populationMode: 'fixed' の場合） */
  fixedPopulation?: number;
  /** リスポーン時のデッキ継承設定 */
  respawnConfig?: RespawnConfig;
}

/**
 * グループ統計
 */
export interface GroupStats {
  groupId: GroupId;
  // 人口
  population: number;
  aliveCount: number;
  // 対戦成績（vsグループ別）
  winsVsGroup: Map<GroupId, number>;
  lossesVsGroup: Map<GroupId, number>;
  // 総合成績
  totalWins: number;
  totalLosses: number;
  // レーティング
  averageRating: number;
  maxRating: number;
  minRating: number;
  // 人口統計
  partnerCount: number;   // パートナーありの人数
  birthCount: number;     // このグループでの出生数
  deathCount: number;     // このグループでの死亡数
}

/**
 * グループモード設定
 */
export interface GroupModeConfig {
  enabled: boolean;         // グループモードが有効か
  groups: GroupConfig[];    // グループ設定リスト
}

/**
 * デフォルトのグループカラー
 */
export const DEFAULT_GROUP_COLORS: string[] = [
  '#ff6b6b',  // 赤 (A)
  '#4dabf7',  // 青 (B)
  '#69db7c',  // 緑 (C)
  '#ffd43b',  // 黄 (D)
  '#da77f2',  // 紫 (E)
  '#ff922b',  // オレンジ (F)
  '#20c997',  // ティール (G)
  '#748ffc',  // インディゴ (H)
];

/**
 * グループIDを生成（A, B, C, ...）
 */
export function generateGroupId(index: number): GroupId {
  return String.fromCharCode(65 + index); // A=65, B=66, ...
}

/**
 * デフォルトのグループ名を生成
 */
export function generateGroupName(groupId: GroupId): string {
  return `グループ${groupId}`;
}

/**
 * グループ設定を作成
 */
export function createGroupConfig(
  index: number,
  cardPoolConfig: CardPoolConfig,
  initialNpcCount: number = 10,
  customName?: string,
  customColor?: string
): GroupConfig {
  const id = generateGroupId(index);
  return {
    id,
    name: customName || generateGroupName(id),
    cardPoolConfig,
    initialNpcCount,
    color: customColor || DEFAULT_GROUP_COLORS[index % DEFAULT_GROUP_COLORS.length],
  };
}

/**
 * 空のグループ統計を作成
 */
export function createEmptyGroupStats(groupId: GroupId): GroupStats {
  return {
    groupId,
    population: 0,
    aliveCount: 0,
    winsVsGroup: new Map(),
    lossesVsGroup: new Map(),
    totalWins: 0,
    totalLosses: 0,
    averageRating: 1000,
    maxRating: 1000,
    minRating: 1000,
    partnerCount: 0,
    birthCount: 0,
    deathCount: 0,
  };
}

/**
 * デフォルトのグループモード設定（無効）
 */
export function createDefaultGroupModeConfig(): GroupModeConfig {
  return {
    enabled: false,
    groups: [],
  };
}

/**
 * 属性ベースのグループ設定を作成（例：炎水、光闘、原始未来）
 */
export function createAttributeBasedGroups(
  groupCount: number,
  npcPerGroup: number = 10
): GroupConfig[] {
  const attributeSets = [
    { attributes: ['炎', '水'], name: '炎水連合' },
    { attributes: ['光', '闇'], name: '光闘連合' },
    { attributes: ['原始', '未来'], name: '原始未来連合' },
    { attributes: ['炎', '光'], name: '炎光連合' },
    { attributes: ['水', '闇'], name: '水闘連合' },
    { attributes: ['原始'], name: '原始の民' },
    { attributes: ['未来'], name: '未来の民' },
    { attributes: ['炎'], name: '炎の一族' },
  ];

  const groups: GroupConfig[] = [];
  for (let i = 0; i < groupCount && i < attributeSets.length; i++) {
    const { attributes, name } = attributeSets[i];
    groups.push(
      createGroupConfig(
        i,
        { allowedAttributes: attributes },
        npcPerGroup,
        name
      )
    );
  }

  return groups;
}

// === 非対称進化モード用ヘルパー関数 ===

/**
 * デフォルトの戦闘設定（通常モード互換）
 */
export function createDefaultBattleConfig(): BattleConfig {
  return {
    internalBattle: false,  // 通常グループモードでは内部戦闘なし
    externalBattle: true,   // 外部戦闘のみ
    recordInternal: false,
    recordExternal: true,   // 外部戦闘を記録
  };
}

/**
 * 非対称進化モード用の戦闘設定（汎用強化グループ向け）
 * - 内部戦闘あり、外部戦闘あり
 * - 内部勝敗のみ記録
 */
export function createInternalFocusBattleConfig(): BattleConfig {
  return {
    internalBattle: true,
    externalBattle: true,
    recordInternal: true,
    recordExternal: false,
  };
}

/**
 * 非対称進化モード用の戦闘設定（対策特化グループ向け）
 * - 内部戦闘なし、外部戦闘のみ
 * - 外部勝敗のみ記録
 */
export function createExternalFocusBattleConfig(): BattleConfig {
  return {
    internalBattle: false,
    externalBattle: true,
    recordInternal: false,
    recordExternal: true,
  };
}

/**
 * デフォルトのリスポーン設定
 */
export function createDefaultRespawnConfig(): RespawnConfig {
  return {
    selfCardRatio: 0.5,    // 50% 死亡NPCの上位カード
    groupCardRatio: 0.3,   // 30% グループ上位NPCのカード
    randomCardRatio: 0.2,  // 20% ランダム
  };
}

/**
 * グループが非対称進化モードかどうか
 */
export function isAsymmetricEvolutionGroup(group: GroupConfig): boolean {
  return group.battleConfig !== undefined || group.populationMode === 'fixed';
}

/**
 * グループの戦闘設定を取得（未設定時はデフォルト）
 */
export function getGroupBattleConfig(group: GroupConfig): BattleConfig {
  return group.battleConfig || createDefaultBattleConfig();
}

/**
 * グループのリスポーン設定を取得（未設定時はデフォルト）
 */
export function getGroupRespawnConfig(group: GroupConfig): RespawnConfig {
  return group.respawnConfig || createDefaultRespawnConfig();
}

/**
 * グループが固定人口モードかどうか
 */
export function isFixedPopulationGroup(group: GroupConfig): boolean {
  return group.populationMode === 'fixed';
}
